import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, ToggleButton, ToggleButtonGroup } from '@backstage/ui';
import styles from '../compliance-page.module.css';

/** One selectable framework (RuleSet) with its computed compliance stats. */
export interface FrameworkInfo {
  /** Entity name — the stable selection key. */
  name: string;
  /** Display title (metadata.title ?? name). */
  title: string;
  type: 'benchmark' | 'controls';
  rulesTotal: number;
  failed: number;
  /** Rounded % of passed rules; null when no rules are mapped. */
  pctPassed: number | null;
}

interface ComplianceFrameworksDialogProps {
  open: boolean;
  onClose: () => void;
  frameworks: FrameworkInfo[];
  selected: Set<string>;
  onToggle: (name: string) => void;
  onClear: () => void;
}

type TypeFilter = 'all' | 'benchmark' | 'controls';

/**
 * "Frameworks & controls" scope dialog: searchable, type-filterable list of
 * RuleSets; checked rows scope the whole compliance page (mockup 1:1).
 */
export function ComplianceFrameworksDialog(props: Readonly<ComplianceFrameworksDialogProps>) {
  const { open, onClose, frameworks, selected, onToggle, onClear } = props;
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  useEffect(() => {
    if (open) {
      setQuery('');
      setTypeFilter('all');
    }
  }, [open]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return frameworks.filter(
      fw =>
        (typeFilter === 'all' || fw.type === typeFilter) &&
        (!q || fw.title.toLowerCase().includes(q)),
    );
  }, [frameworks, query, typeFilter]);

  const typeBadge = (type: FrameworkInfo['type']) =>
    type === 'benchmark' ? styles.fwTypeBenchmark : styles.fwTypeControls;

  return (
    <Dialog
      isOpen={open}
      onOpenChange={nextOpen => !nextOpen && onClose()}
      width={580}
      aria-label="Frameworks and controls"
    >
      <div style={{ padding: 'calc(var(--bui-space) * 4)' }}>
        <div className={styles.fwDialogToolbar}>
          <label className={styles.search} style={{ flex: 1, minWidth: 150 }}>
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search frameworks…"
              aria-label="Search frameworks"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          <ToggleButtonGroup
            aria-label="Framework type"
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={new Set([typeFilter])}
            onSelectionChange={keys => {
              const next = [...keys][0] as TypeFilter | undefined;
              setTypeFilter(next ?? 'all');
            }}
          >
            {(
              [
                ['all', 'All'],
                ['benchmark', 'Benchmarks'],
                ['controls', 'Controls'],
              ] as [TypeFilter, string][]
            ).map(([value, label]) => (
              <ToggleButton key={value} id={value} size="small">
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        <div className={styles.fwList}>
          {rows.map(fw => (
            <label key={fw.name} className={styles.fwRow}>
              <input
                type="checkbox"
                checked={selected.has(fw.name)}
                onChange={() => onToggle(fw.name)}
              />
              <span className={`${styles.fwType} ${typeBadge(fw.type)}`}>
                {fw.type}
              </span>
              <span className={styles.fwRowGrow}>
                <span className={styles.fwName}>{fw.title}</span>
                <div className={styles.fwRowFull}>{fw.name}</div>
              </span>
              <span className={styles.fwRowPct}>
                {fw.pctPassed === null ? '–' : `${fw.pctPassed}%`}
              </span>
              <span className={styles.fwRowFail}>{fw.failed} failed</span>
            </label>
          ))}
          {rows.length === 0 && (
            <div className={styles.empty}>No frameworks match</div>
          )}
        </div>

        <div className={styles.fwDialogFoot}>
          <span className={styles.fwMeta}>
            {selected.size} selected · rules of checked frameworks scope the
            whole page
          </span>
          <button type="button" className={styles.chipClear} onClick={onClear}>
            Clear
          </button>
          <Button
            variant="primary"
            style={{ marginLeft: 'auto' }}
            onClick={onClose}
          >
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
